import { useState, useCallback, useEffect, useRef } from 'react';
import { scoreService } from '@/lib/services/ai-score-service';
import { EssayScore, EssayScoreResult } from '@/app/score/types/score';
import { StructuredEssayData, WritingTaskType } from '@/types/writing';
import {
  QuestionType,
  QuestionNote,
  Task1Type,
  VisualType,
} from '@/types/task-bank';

interface UseWritingScoreProps {
  userId: string | undefined;
  currentEssayId: string | null;
  isProUser: boolean;
  onScoreFetched?: () => void;
  onHistoryCreated?: () => void;
}

export function useWritingScore({
  userId,
  currentEssayId,
  onScoreFetched,
  onHistoryCreated,
}: UseWritingScoreProps) {
  // Score data
  const [essayScore, setEssayScore] = useState<EssayScore | null>(null);
  const [scoreError, setScoreError] = useState<string | null>(null);

  // UI state
  const [isScoring, setIsScoring] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const [isCheckingHistory, setIsCheckingHistory] = useState(true);
  const [showMinimalChangeDialog, setShowMinimalChangeDialog] = useState(false);
  const [changeType, setChangeType] = useState<
    'no_change' | 'minimal_change' | null
  >(null);

  const lastCheckedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!currentEssayId) {
      setIsCheckingHistory(false);
      return;
    }

    if (lastCheckedIdRef.current === currentEssayId) {
      return;
    }

    async function checkForExistingScore() {
      setIsCheckingHistory(true);
      try {
        const result = await scoreService.getLatest(currentEssayId);
        if (result) {
          setEssayScore(result);
          setHasHistory(true);
          if (onScoreFetched) onScoreFetched();
        }
        lastCheckedIdRef.current = currentEssayId;
      } catch (error) {
        console.error(
          '[useWritingScore] Error fetching existing score:',
          error
        );
      } finally {
        setIsCheckingHistory(false);
      }
    }

    checkForExistingScore();
  }, [currentEssayId, onScoreFetched]);

  const processScoreResult = useCallback(
    (result: EssayScoreResult) => {
      if (result.error) {
        setScoreError(result.error);
        setEssayScore(null);
        setShowMinimalChangeDialog(false);
        setChangeType(null);
        return;
      }

      if (result.status === 'unchanged_identical') {
        setShowMinimalChangeDialog(true);
        setChangeType('no_change');
        result.changeType = 'no_change';
      } else if (result.status === 'unchanged_minimal') {
        setShowMinimalChangeDialog(true);
        setChangeType('minimal_change');
        result.changeType = 'minimal_change';
      } else {
        setShowMinimalChangeDialog(false);
        setChangeType(null);
        result.changeType = null;
      }

      setEssayScore(result);
      setHasHistory(true);
      if (onScoreFetched) onScoreFetched();
      if (onHistoryCreated && result.status === 'updated') onHistoryCreated();
    },
    [onHistoryCreated, onScoreFetched]
  );

  const clearScore = useCallback(() => {
    setEssayScore(null);
    setScoreError(null);
    setShowMinimalChangeDialog(false);
    setChangeType(null);
  }, []);

  const fetchNewScore = useCallback(
    async (
      prompt: string,
      essayData: StructuredEssayData,
      taskType: WritingTaskType = 'task2',
      questionType?: QuestionType | '',
      questionNote?: QuestionNote | '',
      task1Type?: Task1Type,
      visualType?: VisualType
    ) => {
      if (!userId || !currentEssayId) {
        setScoreError(
          !userId ? 'You must be logged in.' : 'Essay must be saved first.'
        );
        return;
      }
      if (
        !prompt ||
        !essayData ||
        essayData.introduction === undefined ||
        essayData.bodyParagraphs === undefined ||
        essayData.conclusion === undefined
      ) {
        setScoreError('Prompt and all essay sections are required.');
        clearScore();
        return;
      }

      setIsScoring(true);
      setScoreError(null);
      setShowMinimalChangeDialog(false);
      setChangeType(null);

      try {
        const result = await scoreService.fetchNew(
          userId,
          currentEssayId,
          prompt,
          essayData,
          taskType,
          questionType,
          questionNote,
          task1Type,
          visualType
        );
        processScoreResult(result);
      } catch (error: unknown) {
        console.error(
          '[useWritingScore] Unexpected error calling scoreService.fetchNew:',
          error
        );
        setScoreError(
          'An unexpected client-side error occurred. ' +
            (error instanceof Error ? error.message : '')
        );
        setEssayScore(null);
        setShowMinimalChangeDialog(false);
        setChangeType(null);
      } finally {
        setIsScoring(false);
      }
    },
    [userId, currentEssayId, processScoreResult, clearScore]
  );

  const checkScoringRequirements = useCallback(
    (essayData: StructuredEssayData, taskType: WritingTaskType = 'task2') => {
      const requirements = [];

      const introWords = essayData.introduction
        ? essayData.introduction.trim().split(/\s+/).length
        : 0;
      const bodyWords =
        essayData.bodyParagraphs?.reduce(
          (total, p) =>
            total + (p.content ? p.content.trim().split(/\s+/).length : 0),
          0
        ) || 0;
      const conclusionWords = essayData.conclusion
        ? essayData.conclusion.trim().split(/\s+/).length
        : 0;
      const totalWords = introWords + bodyWords + conclusionWords;

      // Use different minimum word counts based on task type
      const minWordCount = taskType === 'task1' ? 150 : 250;

      if (totalWords < minWordCount) {
        requirements.push(
          `Minimum ${minWordCount} words required (current: ${totalWords})`
        );
      }

      if (
        !essayData.introduction ||
        essayData.introduction.trim().length === 0
      ) {
        requirements.push('Introduction is required');
      }

      if (!essayData.bodyParagraphs || essayData.bodyParagraphs.length === 0) {
        const paragraphTerm =
          taskType === 'task1' ? 'detail paragraph' : 'body paragraph';
        requirements.push(`At least one ${paragraphTerm} is required`);
      } else {
        const emptyParagraphs = essayData.bodyParagraphs.filter(
          (p) => !p.content || p.content.trim().length === 0
        );
        if (emptyParagraphs.length > 0) {
          const paragraphTerm =
            taskType === 'task1' ? 'detail paragraphs' : 'body paragraphs';
          requirements.push(`All ${paragraphTerm} must have content`);
        }
      }

      if (!essayData.conclusion || essayData.conclusion.trim().length === 0) {
        const sectionTerm = taskType === 'task1' ? 'Overview' : 'Conclusion';
        requirements.push(`${sectionTerm} is required`);
      }

      return requirements;
    },
    []
  );

  return {
    score: essayScore,
    scoreError,

    isScoring,
    hasHistory,
    isCheckingHistory,
    showMinimalChangeDialog,
    changeType,

    fetchNewScore,
    clearScore,
    checkScoringRequirements,

    setShowMinimalChangeDialog,
  };
}
